/**
 * Stroke icons on a 24-unit grid, drawn to read at 16-24 px with a 1.7 stroke.
 * Platform and device glyphs are generic shapes, not trademarks.
 */
export type Shape =
  | { readonly d: string; readonly fill?: boolean }
  | { readonly c: readonly [cx: number, cy: number, r: number]; readonly fill?: boolean };

const frame =
  'M3 5.5A1.5 1.5 0 0 1 4.5 4h15A1.5 1.5 0 0 1 21 5.5v13a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18.5z';

export const icons = {
  // Navigation and product
  devices: [
    {
      d: 'M3 5.5A1.5 1.5 0 0 1 4.5 4h15A1.5 1.5 0 0 1 21 5.5v9a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 14.5z',
    },
    { d: 'M8 20h8' },
    { d: 'M12 16v4' },
  ],
  terminal: [{ d: frame }, { d: 'm7 9 3.5 3L7 15' }, { d: 'M12.5 15H17' }],
  people: [
    { c: [9, 8, 3.25] },
    { d: 'M3 20v-1.25a6 6 0 0 1 12 0V20' },
    { d: 'M15.5 4.6a3.25 3.25 0 0 1 0 6.3' },
    { d: 'M21 20v-1.25a6 6 0 0 0-3.6-5.5' },
  ],
  settings: [
    { d: 'M4 7h9' },
    { d: 'M17.5 7H20' },
    { c: [15, 7, 2.25] },
    { d: 'M4 17h3' },
    { d: 'M11.5 17H20' },
    { c: [9, 17, 2.25] },
  ],
  files: [
    {
      d: 'M3.5 7A1.5 1.5 0 0 1 5 5.5h4.2a1.5 1.5 0 0 1 1.2.6L11.8 8H19a1.5 1.5 0 0 1 1.5 1.5v8A1.5 1.5 0 0 1 19 19H5a1.5 1.5 0 0 1-1.5-1.5z',
    },
  ],
  logs: [{ d: 'M4 6h16' }, { d: 'M4 10h11' }, { d: 'M4 14h16' }, { d: 'M4 18h8' }],
  resources: [{ d: 'M3 12h4l3-7 4 14 3-7h4' }],

  // Device glyphs (chosen per device by the person)
  laptop: [
    { d: 'M4.5 6.5A1.5 1.5 0 0 1 6 5h12a1.5 1.5 0 0 1 1.5 1.5V15h-15z' },
    { d: 'M2.5 18h19' },
  ],
  tower: [
    {
      d: 'M7 3.5h10A1.5 1.5 0 0 1 18.5 5v14a1.5 1.5 0 0 1-1.5 1.5H7A1.5 1.5 0 0 1 5.5 19V5A1.5 1.5 0 0 1 7 3.5z',
    },
    { d: 'M9 7.5h6' },
    { d: 'M9 10.5h6' },
    { d: 'M12 16.5h.01' },
  ],
  server: [
    {
      d: 'M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v3A1.5 1.5 0 0 1 18.5 10h-13A1.5 1.5 0 0 1 4 8.5z',
    },
    {
      d: 'M4 15.5A1.5 1.5 0 0 1 5.5 14h13a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18.5z',
    },
    { d: 'M7.5 7h.01' },
    { d: 'M7.5 17h.01' },
  ],
  nas: [
    {
      d: 'M4 6.5A1.5 1.5 0 0 1 5.5 5h13A1.5 1.5 0 0 1 20 6.5v11a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5z',
    },
    { d: 'M4 12h16' },
    { d: 'M7.5 8.5h.01' },
    { d: 'M7.5 15.5h.01' },
    { d: 'M11 8.5h5.5' },
    { d: 'M11 15.5h5.5' },
  ],
  mini: [
    {
      d: 'M4 9.5A1.5 1.5 0 0 1 5.5 8h13a1.5 1.5 0 0 1 1.5 1.5v5a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 14.5z',
    },
    { d: 'M7.5 12h.01' },
    { d: 'M11 12h5.5' },
  ],
  gpu: [
    {
      d: 'M3 8.5A1.5 1.5 0 0 1 4.5 7h15A1.5 1.5 0 0 1 21 8.5v6a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 14.5z',
    },
    { c: [8.5, 11.5, 2.25] },
    { c: [15.5, 11.5, 2.25] },
    { d: 'M6 18.5h8' },
  ],
  tv: [
    {
      d: 'M3 5.5A1.5 1.5 0 0 1 4.5 4h15A1.5 1.5 0 0 1 21 5.5v10a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 15.5z',
    },
    { d: 'm8.5 20 1.5-3' },
    { d: 'm15.5 20-1.5-3' },
  ],
  tablet: [
    {
      d: 'M6 3.5h12A1.5 1.5 0 0 1 19.5 5v14a1.5 1.5 0 0 1-1.5 1.5H6A1.5 1.5 0 0 1 4.5 19V5A1.5 1.5 0 0 1 6 3.5z',
    },
    { d: 'M11 18h2' },
  ],
  phone: [
    {
      d: 'M8.5 2.5h7A1.5 1.5 0 0 1 17 4v16a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 7 20V4a1.5 1.5 0 0 1 1.5-1.5z',
    },
    { d: 'M11 18.5h2' },
  ],
  cloud: [{ d: 'M6.5 18.5a3.5 3.5 0 0 1-.4-6.97A5.5 5.5 0 0 1 16.9 9.5a3.75 3.75 0 0 1 .6 9z' }],
  gamepad: [
    {
      d: 'M7 8h10a4.5 4.5 0 0 1 4.5 4.5l-.7 3.9a2.2 2.2 0 0 1-3.8 1L15 15.5H9l-2 1.9a2.2 2.2 0 0 1-3.8-1l-.7-3.9A4.5 4.5 0 0 1 7 8z',
    },
    { d: 'M8.5 10.75v3' },
    { d: 'M7 12.25h3' },
    { d: 'M15.5 11.5h.01' },
    { d: 'M17.5 13h.01' },
  ],
  router: [
    { d: 'M4 13.5h16a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1z' },
    { d: 'M7 16.5h.01' },
    { d: 'M10 16.5h.01' },
    { d: 'M17 13.5V6.5' },
    { d: 'm14.5 8.5 2.5-2.5 2.5 2.5' },
  ],
  windows: [
    { d: 'M4 5.5 11 4.6v6.4H4z' },
    { d: 'M12.5 4.4 20 3.5V11h-7.5z' },
    { d: 'M4 12.5h7v6.4L4 18z' },
    { d: 'M12.5 12.5H20v7.5l-7.5-.9z' },
  ],

  // Actions and states
  search: [{ c: [11, 11, 6] }, { d: 'm20 20-3.6-3.6' }],
  refresh: [{ d: 'M20 12a8 8 0 1 1-2.34-5.66' }, { d: 'M20 4v5h-5' }],
  plus: [{ d: 'M12 5v14' }, { d: 'M5 12h14' }],
  minus: [{ d: 'M5 12h14' }],
  back: [{ d: 'm14.5 5.5-6.5 6.5 6.5 6.5' }],
  chevronRight: [{ d: 'm9.5 5.5 6.5 6.5-6.5 6.5' }],
  chevronDown: [{ d: 'm5.5 9.5 6.5 6.5 6.5-6.5' }],
  chevronUp: [{ d: 'm5.5 14.5 6.5-6.5 6.5 6.5' }],
  external: [{ d: 'M7 17 17 7' }, { d: 'M8.5 7H17v8.5' }],
  check: [{ d: 'm5 12.5 4.5 4.5L19.5 7' }],
  close: [{ d: 'M6 6l12 12' }, { d: 'M18 6 6 18' }],
  more: [
    { c: [5, 12, 1.4], fill: true },
    { c: [12, 12, 1.4], fill: true },
    { c: [19, 12, 1.4], fill: true },
  ],
  edit: [{ d: 'M4 20h4l10.5-10.5a2.1 2.1 0 0 0-3-3L5 17z' }, { d: 'm13.5 6.5 3 3' }],
  grid: [
    { d: 'M4 4h6.5v6.5H4z' },
    { d: 'M13.5 4H20v6.5h-6.5z' },
    { d: 'M4 13.5h6.5V20H4z' },
    { d: 'M13.5 13.5H20V20h-6.5z' },
  ],
  list: [
    { d: 'M8 6h12' },
    { d: 'M8 12h12' },
    { d: 'M8 18h12' },
    { d: 'M4 6h.01' },
    { d: 'M4 12h.01' },
    { d: 'M4 18h.01' },
  ],
  sort: [{ d: 'M7 4v16' }, { d: 'm4 17 3 3 3-3' }, { d: 'M17 20V4' }, { d: 'm14 7 3-3 3 3' }],
  folder: [
    {
      d: 'M3.5 7A1.5 1.5 0 0 1 5 5.5h4.2a1.5 1.5 0 0 1 1.2.6L11.8 8H19a1.5 1.5 0 0 1 1.5 1.5v8A1.5 1.5 0 0 1 19 19H5a1.5 1.5 0 0 1-1.5-1.5z',
    },
  ],
  file: [
    {
      d: 'M7 3.5h7l4.5 4.5v11A1.5 1.5 0 0 1 17 20.5H7A1.5 1.5 0 0 1 5.5 19V5A1.5 1.5 0 0 1 7 3.5z',
    },
    { d: 'M14 3.5V8h4.5' },
  ],
  upload: [{ d: 'M12 16V5' }, { d: 'm7.5 9.5 4.5-4.5 4.5 4.5' }, { d: 'M4.5 19.5h15' }],
  download: [{ d: 'M12 4v11' }, { d: 'm7.5 10.5 4.5 4.5 4.5-4.5' }, { d: 'M4.5 19.5h15' }],
  building: [
    { d: 'M5 20V5.5A1.5 1.5 0 0 1 6.5 4h7A1.5 1.5 0 0 1 15 5.5V20' },
    { d: 'M15 9h3.5A1.5 1.5 0 0 1 20 10.5V20' },
    { d: 'M3.5 20h17' },
    { d: 'M8.5 8h3' },
    { d: 'M8.5 12h3' },
    { d: 'M8.5 16h3' },
  ],
  tag: [{ d: 'M4 4h7l9 9-7 7-9-9z' }, { d: 'M8 8h.01' }],
  camera: [
    {
      d: 'M4 8.5A1.5 1.5 0 0 1 5.5 7h2.6l1.4-2h5l1.4 2h2.6A1.5 1.5 0 0 1 20 8.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5z',
    },
    { c: [12, 12.5, 3.25] },
  ],
  keyboard: [
    {
      d: 'M3 7.5A1.5 1.5 0 0 1 4.5 6h15A1.5 1.5 0 0 1 21 7.5v9a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 16.5z',
    },
    { d: 'M7 10h.01' },
    { d: 'M11 10h.01' },
    { d: 'M15 10h.01' },
    { d: 'M8 14h8' },
  ],
  clipboard: [
    { d: 'M9 4.5h6v2.5H9z' },
    {
      d: 'M15 5.5h2A1.5 1.5 0 0 1 18.5 7v12a1.5 1.5 0 0 1-1.5 1.5H7A1.5 1.5 0 0 1 5.5 19V7A1.5 1.5 0 0 1 7 5.5h2',
    },
  ],
  type: [{ d: 'M6 5h12' }, { d: 'M12 5v14' }, { d: 'M9 19h6' }],
  panel: [{ d: frame }, { d: 'M15 4v16' }],
  stats: [{ d: 'M5 20v-6' }, { d: 'M12 20V4' }, { d: 'M19 20v-10' }],
  lock: [
    {
      d: 'M6 11h12a1.5 1.5 0 0 1 1.5 1.5v6A1.5 1.5 0 0 1 18 20H6a1.5 1.5 0 0 1-1.5-1.5v-6A1.5 1.5 0 0 1 6 11z',
    },
    { d: 'M8.5 11V8a3.5 3.5 0 0 1 7 0v3' },
  ],
  trash: [
    { d: 'M5 7h14' },
    { d: 'M9 7V4.5h6V7' },
    { d: 'M7 7l.8 12.2a1.5 1.5 0 0 0 1.5 1.3h5.4a1.5 1.5 0 0 0 1.5-1.3L17 7' },
  ],
  volume: [
    { d: 'M4 9.5v5h3l4 3.5v-12l-4 3.5z' },
    { d: 'M15.5 9a4 4 0 0 1 0 6' },
    { d: 'M18 6.5a7.5 7.5 0 0 1 0 11' },
  ],
  zoomIn: [{ c: [11, 11, 6] }, { d: 'm20 20-3.6-3.6' }, { d: 'M11 8.5v5' }, { d: 'M8.5 11h5' }],
  zoomOut: [{ c: [11, 11, 6] }, { d: 'm20 20-3.6-3.6' }, { d: 'M8.5 11h5' }],
  collapse: [{ d: 'M9 4v5H4' }, { d: 'M15 4v5h5' }, { d: 'M9 20v-5H4' }, { d: 'M15 20v-5h5' }],
  question: [
    { c: [12, 12, 8.5] },
    { d: 'M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 1-1 1.7' },
    { d: 'M12 17h.01' },
  ],
  sun: [
    { c: [12, 12, 3.75] },
    { d: 'M12 2.5v2' },
    { d: 'M12 19.5v2' },
    { d: 'M2.5 12h2' },
    { d: 'M19.5 12h2' },
    { d: 'm5.3 5.3 1.4 1.4' },
    { d: 'm17.3 17.3 1.4 1.4' },
    { d: 'm5.3 18.7 1.4-1.4' },
    { d: 'm17.3 6.7 1.4-1.4' },
  ],
  moon: [{ d: 'M20 14.2A8 8 0 1 1 9.8 4a6.4 6.4 0 0 0 10.2 10.2z' }],
  system: [{ c: [12, 12, 8.5] }, { d: 'M12 3.5a8.5 8.5 0 0 1 0 17z', fill: true }],
  shield: [{ d: 'M12 3 4.5 6v5.6c0 4.4 3.1 7.7 7.5 9.4 4.4-1.7 7.5-5 7.5-9.4V6z' }],
  key: [{ c: [8, 15, 4] }, { d: 'm10.8 12.2 8.7-8.7' }, { d: 'm16.5 5 2.5 2.5' }],
  warning: [
    {
      d: 'M10.7 3.9 2.6 18.1a1.5 1.5 0 0 0 1.3 2.2h16.2a1.5 1.5 0 0 0 1.3-2.2L13.3 3.9a1.5 1.5 0 0 0-2.6 0z',
    },
    { d: 'M12 9v4.5' },
    { d: 'M12 17h.01' },
  ],
  info: [{ c: [12, 12, 8.5] }, { d: 'M12 11v5' }, { d: 'M12 8h.01' }],
  power: [{ d: 'M12 3.5V12' }, { d: 'M17.7 7.3a8 8 0 1 1-11.4 0' }],
  user: [{ c: [12, 8.5, 3.75] }, { d: 'M5 20a7 7 0 0 1 14 0' }],
  logout: [
    { d: 'M9.5 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h3.5' },
    { d: 'm15 8 4 4-4 4' },
    { d: 'M19 12H9.5' },
  ],
  globe: [
    { c: [12, 12, 8.5] },
    { d: 'M3.5 12h17' },
    { d: 'M12 3.5a13 13 0 0 1 0 17' },
    { d: 'M12 3.5a13 13 0 0 0 0 17' },
  ],
  app: [{ d: frame }, { d: 'M4 9h16' }, { d: 'M7 6.5h.01' }],
  eye: [
    { d: 'M2.5 12S6 5.75 12 5.75 21.5 12 21.5 12 18 18.25 12 18.25 2.5 12 2.5 12z' },
    { c: [12, 12, 2.75] },
  ],
  pointer: [{ d: 'm5 4 14.5 6.3-6.3 1.9L11.3 19z' }],
  bolt: [{ d: 'M13 2.5 4.5 14h7l-1 7.5L19.5 10h-7z' }],
  link: [
    { d: 'M10 14a4.5 4.5 0 0 0 6.4 0l2.1-2.1a4.5 4.5 0 0 0-6.4-6.4l-1 1' },
    { d: 'M14 10a4.5 4.5 0 0 0-6.4 0l-2.1 2.1a4.5 4.5 0 0 0 6.4 6.4l1-1' },
  ],
  fullscreen: [{ d: 'M4 9V4h5' }, { d: 'M20 9V4h-5' }, { d: 'M4 15v5h5' }, { d: 'M20 15v5h-5' }],
  clock: [{ c: [12, 12, 8.5] }, { d: 'M12 7.5V12l3 2' }],
  spinner: [{ d: 'M12 3.5a8.5 8.5 0 0 1 8.5 8.5' }],
} as const satisfies Record<string, readonly Shape[]>;

export type IconName = keyof typeof icons;
